import { escapeHtml } from '../util/escapeHtml.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import type { HouseRank, PublicHouseDto } from '@rex-mortis/shared';

/**
 * Public, read-only "scouting card" for a House — reached via #/house/:id from
 * the Annals (Houses tab) and anywhere a House is named. Shows the roster with
 * ranks, total renown, and any holdings. Deliberately a PUBLIC SUBSET: never
 * the treasury or any member's personal details.
 */
export async function renderPublicHouse(root: HTMLElement, params?: Record<string, string>): Promise<void> {
  const id = params?.id ?? '';

  let mainEl: HTMLElement | null = null;
  mountShell(root, (main) => {
    mainEl = main;
    main.innerHTML = `<p style="color: var(--c-ink-soft);">Consulting the heralds…</p>`;
  });
  if (!mainEl) return;
  const main = mainEl as HTMLElement;

  if (!id) {
    main.innerHTML = errorHtml('No House was named.');
    return;
  }

  try {
    const [{ house }, mine] = await Promise.all([
      api.publicHouse(id),
      api.myHouse().catch(() => ({ house: null, application: null })),
    ]);
    main.innerHTML = renderCard(house, mine);
    wire(main, house);
  } catch (err) {
    const notFound = err instanceof HttpError && err.status === 404;
    main.innerHTML = errorHtml(notFound ? 'No such House holds these lands.' : 'The heralds could not produce that record.');
  }
}

const RANK_LABEL: Record<HouseRank, string> = {
  LORD: 'Lord',
  CHANCELLOR: 'Chancellor',
  KNIGHT: 'Knight',
  SQUIRE: 'Squire',
  INITIATE: 'Initiate',
};

type MyHouseLike = { house: { id: string; name: string } | null; application: { houseId: string; houseName: string } | null };

function renderCard(h: PublicHouseDto, mine: MyHouseLike): string {
  const founded = h.createdAt
    ? new Date(h.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  const membersRows = h.members
    .map(
      (m) => `
      <tr>
        <td><a class="lb-name-link" href="#/u/${encodeURIComponent(m.characterId)}"><strong>${escapeHtml(m.name)}</strong></a></td>
        <td>${m.level}</td>
        <td><span class="pill${m.rank === 'LORD' ? ' gold' : ''}">${escapeHtml(RANK_LABEL[m.rank])}</span></td>
      </tr>`,
    )
    .join('');

  const holdingsHtml =
    h.holdings.length > 0
      ? `<div class="ph-holdings">
          ${h.holdings
            .map(
              (hd) => `
            <div class="ph-holding">
              <strong>${escapeHtml(hd.name)}</strong>
              <span class="pill">+${hd.dailyRenown} renown/day</span>
              <div class="ph-holding-blurb">${escapeHtml(hd.blurb)}</div>
            </div>`,
            )
            .join('')}
        </div>`
      : `<p class="ph-muted">This House holds no fiefs.</p>`;

  // Apply controls — server enforces all rules, but we tailor the prompt to the
  // viewer's current standing so it reads sensibly.
  let applyHtml: string;
  if (mine.house && mine.house.id === h.id) {
    applyHtml = `<p class="ph-muted">You are a member of this House.</p>`;
  } else if (mine.house) {
    applyHtml = `<p class="ph-muted">You already belong to <strong>${escapeHtml(mine.house.name)}</strong>. Leave it before petitioning another.</p>`;
  } else if (mine.application && mine.application.houseId === h.id) {
    applyHtml = `<p class="ph-muted">⚜ Your petition to this House is pending a Chancellor's word.</p>`;
  } else if (mine.application) {
    applyHtml = `<p class="ph-muted">You have a pending petition to <strong>${escapeHtml(mine.application.houseName)}</strong>. Withdraw it from your Houses hall first.</p>`;
  } else {
    applyHtml = `
      <div id="ph-flash"></div>
      <div class="ph-apply">
        <input id="ph-msg" type="text" maxlength="280" placeholder="A word for the Chancellor (optional)" />
        <button class="btn gold" id="ph-apply-btn">⚜ Petition to Join</button>
      </div>`;
  }

  const bannerSrc = h.bannerUrl || '/img/banners/house-default.webp';
  return `
    <div class="ppf-back"><a href="#/leaderboards">← The Annals</a></div>
    <div class="card">
      <img class="house-banner" style="margin-bottom:var(--s-3);" src="${escapeHtml(bannerSrc)}" alt="${escapeHtml(h.name)} banner" onerror="this.onerror=null;this.src='/img/banners/houses.webp'" />
      <h1 style="margin-bottom:2px;">${escapeHtml(h.name)} <span class="pill">[${escapeHtml(h.tag)}]</span></h1>
      ${h.motto ? `<p style="font-style:italic; color: var(--c-ink-soft); margin-top:0;">“${escapeHtml(h.motto)}”</p>` : ''}
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin: var(--s-3) 0;">
        <span class="pill gold">Renown <strong>${h.renown.toLocaleString()}</strong></span>
        <span class="pill">Members <strong>${h.memberCount}</strong></span>
        <span class="pill">Founded <strong>${escapeHtml(founded)}</strong></span>
      </div>
      ${applyHtml}
    </div>

    <div class="card">
      <h2 class="ppf-heading">Members</h2>
      <div class="table-scroll">
        <table class="lb-table">
          <thead><tr><th>Member</th><th>Level</th><th>Rank</th></tr></thead>
          <tbody>${membersRows}</tbody>
        </table>
      </div>
    </div>

    <div class="card">
      <h2 class="ppf-heading">Holdings</h2>
      ${holdingsHtml}
    </div>

    <style>
      .ph-apply { display:flex; gap: var(--s-2); flex-wrap:wrap; align-items:center; }
      .ph-apply input { flex:1 1 240px; min-width:0; }
      .ph-muted { color: var(--c-ink-soft); font-style: italic; }
      .ph-holdings { display:flex; flex-direction:column; gap: var(--s-3); }
      .ph-holding { padding: var(--s-2) 0; border-bottom: 1px solid var(--c-rule); }
      .ph-holding:last-child { border-bottom: none; }
      .ph-holding-blurb { color: var(--c-ink-soft); font-size: 0.92rem; margin-top: 2px; }
    </style>
  `;
}

function wire(main: HTMLElement, h: PublicHouseDto): void {
  const btn = main.querySelector<HTMLButtonElement>('#ph-apply-btn');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const flash = main.querySelector<HTMLDivElement>('#ph-flash');
    const msg = main.querySelector<HTMLInputElement>('#ph-msg')?.value.trim() ?? '';
    btn.disabled = true;
    try {
      await api.applyToHouse(msg ? { houseId: h.id, message: msg } : { houseId: h.id });
      if (flash) flash.innerHTML = `<div class="alert success">Your petition has been sent to the Chancellors of ${escapeHtml(h.name)}.</div>`;
      const apply = main.querySelector<HTMLElement>('.ph-apply');
      if (apply) apply.remove();
    } catch (err) {
      btn.disabled = false;
      if (flash) flash.innerHTML = `<div class="alert danger">${escapeHtml(err instanceof HttpError ? err.message : 'Could not send your petition.')}</div>`;
    }
  });
}

function errorHtml(msg: string): string {
  return `
    <div class="ppf-back"><a href="#/leaderboards">← The Annals</a></div>
    <div class="card"><p style="color: var(--c-ink-soft);">${escapeHtml(msg)}</p></div>`;
}

